/**
 * Shared section-level utilities for compliance dashboard data.
 * Used by ComplianceDataContext for section-id / title / scope-organisation logic.
 */



import type { ScopeType } from '@/services/complianceAssessmentService';
import {
  Scope,
  Role,
  RequirementWithStatus,
  RequirementComplianceResponse,
} from '@/services/complianceAssessmentService';
import type {
  State as LocationState,
  District as LocationDistrict,
  DispatchArea as LocationDispatchArea,
} from '@/services/locationService';
import { countRequirementAlerts } from '@/utils/complianceStatusCounting';

/** Display order for dashboard sections. */
export const SCOPE_TYPE_ORDER: ScopeType[] = [
  'EverDriven',
  'State',
  'District',
  'Vehicle',
  'Shared',
];

/** Maps a scopeType to its URL-safe section id. */
export const SECTION_ID_MAP: Record<ScopeType, string> = {
  EverDriven: 'everdriven',
  State:      'states',
  District:   'districts',
  Vehicle:    'vehicles',
  Shared:     'shared',
};

/** Human-readable section titles shown in the UI. */
export const SECTION_TITLES: Record<ScopeType, string> = {
  EverDriven: 'EverDriven Requirements',
  State:      'States Requirements',
  District:   'Districts Requirements',
  Vehicle:    'Vehicles Requirements',
  Shared:     'Shared Requirements',
};

/** Returns the URL-safe section id for a given scopeType. */
export function getSectionId(scopeType: ScopeType | string): string {
  // Only use SECTION_ID_MAP for valid ScopeType values
  if (
    typeof scopeType === 'string' &&
    Object.prototype.hasOwnProperty.call(SECTION_ID_MAP, scopeType)
  ) {
    return SECTION_ID_MAP[scopeType as ScopeType];
  }
  // Fallback: lowercase any other string
  if (typeof scopeType === 'string') {
    return scopeType.toLowerCase();
  }
  return '';
}

export interface RequirementsByScope {
  [scopeType: string]: {
    scopeId: string;
    scopeName: string;
    roles: Role[];
  };
}

export interface DashboardAlerts {
  error: number;         // Out of Compliance
  warning: number;       // Warning
  // Retired category — always 0. Requirements persisted under it now tally as error.
  incomplete: number;
  complete: number;
}

export interface DashboardSection {
  id: string;
  scopeType: ScopeType;
  title: string;
  // null when the section has no impacting requirements — there is nothing to
  // score, so the UI shows "N/A" rather than a misleading 0%.
  compliancePercentage: number | null;
  alerts: DashboardAlerts;
  impactingAlerts: DashboardAlerts;
  nonImpactingAlerts: DashboardAlerts;
  hasIssue: boolean;
}

function isImpactingRequirement(requirement: RequirementWithStatus): boolean {
  return requirement.requirement.impacting === true;
}

function countAlertsByImpact(requirements: RequirementWithStatus[]): {
  impactingAlerts: DashboardAlerts;
  nonImpactingAlerts: DashboardAlerts;
} {
  return {
    impactingAlerts: countRequirementAlerts(requirements.filter(isImpactingRequirement)),
    nonImpactingAlerts: countRequirementAlerts(requirements.filter((r) => !isImpactingRequirement(r))),
  };
}

const emptyAlerts = (): DashboardAlerts => ({
  error: 0, warning: 0, incomplete: 0, complete: 0,
});

/** Adds every bucket of `source` into `target` in place. */
function addAlerts(target: DashboardAlerts, source: DashboardAlerts): void {
  target.error += source.error;
  target.warning += source.warning;
  target.incomplete += source.incomplete;
  target.complete += source.complete;
}

/**
 * Builds an alerts bucket from scope-level impact counts when the API provides any
 * of them; otherwise defers to the requirement-derived `fallback` (computed lazily).
 */
function impactAlertsFromScopeCounts(
  counts: {
    compliant?: number | undefined;
    nonCompliant?: number | undefined;
    warning?: number | undefined;
    inComplete?: number | undefined;
  },
  fallback: () => DashboardAlerts,
): DashboardAlerts {
  const hasApiCounts =
    counts.compliant !== undefined ||
    counts.nonCompliant !== undefined ||
    counts.warning !== undefined ||
    counts.inComplete !== undefined;
  if (!hasApiCounts) return fallback();
  return {
    complete: counts.compliant ?? 0,
    // A count still carried under the retired In-Complete bucket folds into error, matching the
    // assessment service and the Requirements-view alert cards.
    error: (counts.nonCompliant ?? 0) + (counts.inComplete ?? 0),
    warning: counts.warning ?? 0,
    incomplete: 0,
  };
}

/**
 * Get counts from scope's compliance counts if available (new API structure).
 * Falls back to counting from requirements when API totals are absent.
 */
export function getAlertsFromScope(scope: Scope, allRequirements: RequirementWithStatus[]): DashboardAlerts {
  if (
    scope.compliantCount !== undefined ||
    scope.nonCompliantCount !== undefined ||
    scope.warningCount !== undefined ||
    scope.inCompleteCount !== undefined
  ) {
    return {
      complete: scope.compliantCount ?? 0,
      // Retired In-Complete counts fold into error, matching the assessment service.
      error: (scope.nonCompliantCount ?? 0) + (scope.inCompleteCount ?? 0),
      warning: scope.warningCount ?? 0,
      incomplete: 0,
    };
  }
  return countRequirementAlerts(allRequirements);
}

/**
 * Computes dashboard sections from API scopes.
 * Aggregates multiple scopes of the same type into a single dashboard section.
 */
export function computeDashboardSections(scopes: Scope[]): DashboardSection[] {
  const scopesByType: Record<string, Scope[]> = {};
  scopes.forEach((scope) => {
    const type = scope.scopeType;
    scopesByType[type] ??= [];
    scopesByType[type].push(scope);
  });

  return SCOPE_TYPE_ORDER
    .filter((type) => scopesByType[type] && scopesByType[type].length > 0)
    .map((scopeType) => {
      const scopesOfType = scopesByType[scopeType] || [];
      const aggregatedAlerts = emptyAlerts();
      const aggregatedImpactingAlerts = emptyAlerts();
      const aggregatedNonImpactingAlerts = emptyAlerts();
      scopesOfType.forEach((scope) => {
        const scopeRequirements = scope.roles.flatMap((role) => role.requirements);
        const scopeAlerts = getAlertsFromScope(scope, scopeRequirements);

        // Derive the requirement-based impact split at most once, and only if a
        // branch actually needs it (i.e. the API omitted the scope-level counts).
        let impactFallback: ReturnType<typeof countAlertsByImpact> | null = null;
        const getImpactFallback = () =>
          (impactFallback ??= countAlertsByImpact(scopeRequirements));

        const scopeImpactAlerts = impactAlertsFromScopeCounts(
          {
            compliant: scope.impactingCompliantCount,
            nonCompliant: scope.impactingNonCompliantCount,
            warning: scope.impactingWarningCount,
            inComplete: scope.impactingInCompleteCount,
          },
          () => getImpactFallback().impactingAlerts,
        );
        const scopeNonImpactAlerts = impactAlertsFromScopeCounts(
          {
            compliant: scope.nonImpactingCompliantCount,
            nonCompliant: scope.nonImpactingNonCompliantCount,
            warning: scope.nonImpactingWarningCount,
            inComplete: scope.nonImpactingInCompleteCount,
          },
          () => getImpactFallback().nonImpactingAlerts,
        );

        addAlerts(aggregatedAlerts, scopeAlerts);
        addAlerts(aggregatedImpactingAlerts, scopeImpactAlerts);
        addAlerts(aggregatedNonImpactingAlerts, scopeNonImpactAlerts);
      });
      // A scope section's compliance representation (counts, progress, issue flag) reflects
      // impacting requirements only; non-impacting requirements are informational.
      const total = aggregatedImpactingAlerts.error + aggregatedImpactingAlerts.warning + aggregatedImpactingAlerts.incomplete + aggregatedImpactingAlerts.complete;
      return {
        id: getSectionId(scopeType),
        scopeType,
        title: SECTION_TITLES[scopeType],
        // No impacting requirements means there is nothing outstanding, so the scope is fully compliant.
        compliancePercentage: total > 0 ? Math.round((aggregatedImpactingAlerts.complete / total) * 100) : 100,
        alerts: aggregatedAlerts,
        impactingAlerts: aggregatedImpactingAlerts,
        nonImpactingAlerts: aggregatedNonImpactingAlerts,
        hasIssue: aggregatedImpactingAlerts.error > 0,
      };
    });
}

/**
 * Identity whose requirements the provider is fetching. Defaults to the
 * logged-in user; the Compliance Management screen overrides it with the
 * driver/monitor an SpStaff is assisting. `kind` selects the assessment
 * endpoint — omit or set to 'user' for the user-scoped route, or set to
 * 'serviceProvider' to hit the SP-scoped route (SP requirements + fleet).
 */
export interface ComplianceSubject {
  id: string;
  roles: string[];
  name?: string;
  kind?: 'user' | 'serviceProvider';
  /**
   * The subject's owning SP. Populated when the caller (CSL) already knows
   * the SP for this driver/monitor — either from the row itself
   * (`by-internal-user` grid, where each row carries `serviceProviderId`)
   * or from the parent SP drill-down context. Used by the vehicle Add
   * dialog to stamp the correct SP on the payload for internal (AnyGroup)
   * callers whose JWT does not carry an SP claim.
   */
  serviceProviderId?: string;
}

/**
 * Which assisting role is acting on the subject's behalf. Drives the
 * "Can Complete" column's header, filter label, and predicate so the same
 * code serves SP and (future) Field Operations screens.
 */
export type AssistingRole = 'SP' | 'FO';

/**
 * Role names returned by the backend lookup table for the "Support Completion
 * Assistance" field on a requirement. Used to gate the SP/FO "upload on
 * behalf of" action. Match case-insensitively.
 *
 * Values MUST match the seed rows in the requirement service migration
 * (AddCompletionAssistingRolesTable): 'SP', 'Field Ops', 'Both'.
 */
export const ASSISTING_ROLE_NAMES = {
  SP: 'SP' as const,
  FIELD_OPS: 'Field Ops' as const,
  BOTH: 'Both' as const,
} as const;

/**
 * Maps the high-level `AssistingRole` (SP / FO) used in UI screens to the
 * exact backend lookup name on the requirement. One change here propagates
 * to the column header, filter labels, and Can-Complete predicate.
 */
export const ASSISTING_ROLE_LOOKUP_NAME = {
  SP: ASSISTING_ROLE_NAMES.SP,
  FO: ASSISTING_ROLE_NAMES.FIELD_OPS,
} as const;

/**
 * Human-readable label shown in the UI for each assisting role.
 * 'FO' (Field Operations) is surfaced as 'Internal' on the compliance
 * management screen when an internal user is assisting a driver/monitor.
 */
export const ASSISTING_ROLE_DISPLAY_NAME: Record<AssistingRole, string> = {
  SP: 'SP',
  FO: 'FO',
} as const;

/**
 * Geographic scope of the subject being managed. Passed by
 * ComplianceManagementView so vehicle Add/Edit dialogs opened from
 * Compliance Status Lookup constrain their state / district / dispatch-area
 * pickers to the DRIVER's authorised areas rather than the logged-in
 * SpStaff / Internal user's own scope.
 */
export interface TargetSubjectScope {
  stateIds: string[];
  districtIds: string[];
  dispatchAreaIds: string[];
}

/**
 * Resolved geographic scope of the target subject (driver / monitor being
 * managed). Produced by ComplianceDataProvider — it fires the location-by-ids
 * and dispatch-areas-by-state fetches ONCE at the provider level (parallel
 * via Promise.all) and stores the results here. Both AddNewVehicleDialog and
 * EditVehicleDialog read from this single source, so mounting both dialogs
 * side by side no longer doubles the underlying network calls.
 *
 * `dispatchAreas` is already filtered down to the driver's authorised subset
 * (via `scope.dispatchAreaIds`), so consumers can render them directly
 * without another filter pass.
 */
export interface TargetSubjectAreas {
  states: LocationState[];
  districts: LocationDistrict[];
  dispatchAreas: LocationDispatchArea[];
  isLoading: boolean;
}

/**
 * State shape returned by ComplianceDataProvider via useComplianceDataContext.
 */
export interface ComplianceDataState {
  rawData: RequirementComplianceResponse | null;
  dashboardSections: DashboardSection[];
  requirementsByScope: RequirementsByScope;
  entitySummary: { status: string; score: number } | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  subject: ComplianceSubject;
  // Which role is assisting in the current screen (SP today, FO planned).
  // null in Requirement Overview / non-assist screens; presence acts as the
  // SCA gate. The view derives an effectiveAssistingRole that also falls back
  // to 'SP' for provider-submitter viewers in Requirement Overview.
  assistingRole: AssistingRole | null;
  // Present when the provider is rendered inside Compliance Status Lookup
  // (an SpStaff / Internal user managing SOMEONE ELSE's compliance). The
  // provider hydrates this from the raw `targetSubjectScope` prop by firing
  // /Districts/locations-by-ids and /DispatchAreas/by-states in parallel,
  // ONCE — both Add and Edit Vehicle dialogs then read from this single
  // resolved shape (see useTargetSubjectAreaScope). Absent in Requirement
  // Overview / Manage flows where the login user IS the subject.
  targetSubjectAreas?: TargetSubjectAreas;
}

export function organizeRequirementsByScope(scopes: Scope[]): RequirementsByScope {
  const result: RequirementsByScope = {};
  scopes.forEach((scope, index) => {
    const sectionId = getSectionId(scope.scopeType as ScopeType);
    result[sectionId] = {
      scopeId:   scope.scopeId   || `${scope.scopeType}-${index}`,
      scopeName: scope.scopeName || `${scope.scopeType} Requirements`,
      roles:     scope.roles,
    };
  });
  return result;
}
